import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  ArrowLeft, User, Lock, Mail, Building2, 
  MapPin, Phone, Save, Eye, EyeOff, ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function AccountSettings() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("password");
  const [loading, setLoading] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Neues Passwort muss mindestens 6 Zeichen haben");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers });
      
      toast.success("Passwort erfolgreich geändert!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Ändern des Passworts");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  const isAdmin = user.role === "admin";
  const isSupport = user.role === "support";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
              </svg>
            </div>
            <div>
              <h1 className="font-bold text-lg">SonderCargo.de</h1>
              <p className="text-xs text-slate-400">Medical, Weapons and much more</p>
            </div>
          </Link>
          
          <nav className="flex items-center gap-6">
            <Link to={isAdmin || isSupport ? "/admin" : "/dashboard"} className="text-slate-300 hover:text-white transition">
              {isAdmin || isSupport ? "Dashboard" : "Meine Sendungen"}
            </Link>
            <div className="flex items-center gap-3">
              <span className="text-sm">{user.name}</span>
              <span className="text-xs px-2 py-1 bg-slate-800 rounded">{user.role?.toUpperCase()}</span>
            </div>
          </nav>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück
        </button>

        <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="page-title">
          Kontoeinstellungen
        </h1>
        <p className="text-slate-500 mb-8">
          Verwalten Sie Ihre Kontoinformationen und Sicherheit
        </p>

        <div className="grid md:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 space-y-1">
                <button
                  onClick={() => setActiveTab("profile")}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition ${
                    activeTab === "profile" ? "bg-blue-50 text-blue-700" : "hover:bg-slate-100"
                  }`}
                  data-testid="tab-profile"
                >
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Profil
                  </span>
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setActiveTab("password")}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition ${
                    activeTab === "password" ? "bg-blue-50 text-blue-700" : "hover:bg-slate-100"
                  }`}
                  data-testid="tab-password"
                >
                  <span className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Passwort
                  </span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            {activeTab === "profile" && (
              <Card className="border-0 shadow-sm" data-testid="profile-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-blue-600" />
                    Profilinformationen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-600">Name</Label>
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border">
                        <User className="h-4 w-4 text-slate-400" />
                        <span>{user.name}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-600">E-Mail</Label>
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <span>{user.email}</span>
                      </div>
                    </div>
                    {user.company_name && (
                      <div className="space-y-2">
                        <Label className="text-slate-600">Firma</Label>
                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          <span>{user.company_name}</span>
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label className="text-slate-600">Rolle</Label>
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          user.role === "admin" ? "bg-purple-100 text-purple-700" :
                          user.role === "support" ? "bg-blue-100 text-blue-700" :
                          user.role === "business" ? "bg-green-100 text-green-700" :
                          "bg-slate-100 text-slate-700"
                        }`}>
                          {user.role === "admin" ? "Administrator" :
                           user.role === "support" ? "Support" :
                           user.role === "business" ? "Business-Kunde" :
                           "Privatkunde"}
                        </span>
                      </div>
                    </div>
                    {user.address && (
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-slate-600">Adresse</Label>
                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          <span>{user.address}, {user.postal_code} {user.city}</span>
                        </div>
                      </div>
                    )}
                    {user.phone && (
                      <div className="space-y-2">
                        <Label className="text-slate-600">Telefon</Label>
                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border">
                          <Phone className="h-4 w-4 text-slate-400" />
                          <span>{user.phone}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 pt-4 border-t">
                    Um Ihre Profilinformationen zu ändern, wenden Sie sich bitte an unseren Support.
                  </p>
                </CardContent>
              </Card>
            )}

            {activeTab === "password" && (
              <Card className="border-0 shadow-sm" data-testid="password-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="h-5 w-5 text-blue-600" />
                    Passwort ändern
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleChangePassword} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">Aktuelles Passwort</Label>
                      <div className="relative">
                        <Input
                          id="currentPassword"
                          type={showCurrentPassword ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="Geben Sie Ihr aktuelles Passwort ein"
                          required
                          className="pr-10"
                          data-testid="current-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">Neues Passwort</Label>
                      <div className="relative">
                        <Input
                          id="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Mindestens 6 Zeichen"
                          required
                          minLength={6}
                          className="pr-10"
                          data-testid="new-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Neues Passwort wiederholen"
                        required
                        minLength={6}
                        data-testid="confirm-password-input"
                      />
                    </div>
                    
                    <div className="pt-4 border-t flex justify-end">
                      <Button 
                        type="submit" 
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={loading}
                        data-testid="save-password-btn"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Speichern...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Save className="h-4 w-4" />
                            Passwort speichern
                          </span>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
